import { BffApiClientMolecule } from "@/app/api-client/store"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import type { MagicEdenAuctionHouseOfferRef } from "@anaconda/core/bid/types"
import { getRawAddress } from "@anaconda/core/blockchain/address"
import { Blockchain } from "@anaconda/core/blockchain/types"
import type { Nft } from "@anaconda/core/nft/types"
import { Trans } from "@lingui/react/macro"
import {
  SolanaTokens,
  deserializeSolanaTransaction,
  fitSolanaTransactionToPacket,
  resolveMagicEdenTokenAccount,
  signAndSendSolanaTransaction,
  toFillAssetStandard,
} from "@rarible_int/anaconda_sdk/solana"
import { sonnerToast } from "@rarible_int/anaconda_ui_components"
import { action, withAsync, withCallHook, wrap } from "@reatom/core"
import { PublicKey } from "@solana/web3.js"
import { molecule } from "bunshi"
import { SolanaSdkContainerMoleculeReatom } from "../../blockchains/solana-sdk-container-molecule.reatom"
import { AppToast, SuccessViewTransaction } from "../../toasts/app-toast"
import { getTransactionErrorMessage } from "../events/transaction-event"
import { invalidateNftOffersQueries } from "../order/invalidate-nft-offers-queries"

export interface SolanaAcceptMagicEdenOfferParams {
  nft: Nft
  offer: MagicEdenAuctionHouseOfferRef
}

/**
 * Magic Eden's offers never expire from our side, but their accept call wants
 * the seller's own expiry; -1 is their "no expiry" sentinel.
 */
const NO_SELLER_EXPIRY = -1

export const SolanaAcceptMagicEdenOfferMoleculeReatom = molecule((mol) => {
  const { sdkContainerAtom } = mol(SolanaSdkContainerMoleculeReatom)
  const bffApiClient = mol(BffApiClientMolecule)
  const { solanaAddress: solanaAddressAtom } = mol(WalletConnectMoleculeReatom)

  const acceptMagicEdenOfferActionFactory = () => {
    const acceptAction = action(
      async ({ nft, offer }: SolanaAcceptMagicEdenOfferParams) => {
        const from = solanaAddressAtom()
        const container = sdkContainerAtom()

        if (!from) {
          throw new Error(
            "Solana wallet address is not available to accept an offer",
          )
        }
        if (!container) {
          throw new Error("Solana container is not initialized")
        }
        if (!nft.nftStandard) {
          throw new Error("NFT standard is unknown for this item")
        }

        const connection = container.resolve(SolanaTokens.CONNECTION_ATOM)()
        const signer = container.resolve(SolanaTokens.SIGNER_ATOM)()

        // The seller must occupy a real signer slot in Magic Eden's message;
        // fail here rather than at the wallet with an opaque error.
        if (signer.publicKey?.toBase58() !== from) {
          throw new Error("Solana signer does not match the NFT owner")
        }

        const nftMint = getRawAddress(nft.id)
        // For MPL Core this is the asset address, for SPL/pNFT the owner's ATA
        // — matching what Magic Eden reports as `tokenAddress` on a listing.
        const tokenATA = await wrap(
          resolveMagicEdenTokenAccount({
            connection,
            nftMint: new PublicKey(nftMint),
            owner: new PublicKey(from),
            nftStandard: toFillAssetStandard(nft.nftStandard),
          }),
        )

        const response = await wrap(
          bffApiClient.api.nfts["magic-eden"]["sell-now"].$post({
            json: {
              seller: from,
              buyer: offer.buyer,
              tokenMint: nftMint,
              tokenATA,
              auctionHouseAddress: offer.auctionHouse,
              newPrice: offer.price,
              price: offer.price,
              sellerExpiry: NO_SELLER_EXPIRY,
              buyerExpiry: offer.expiry,
              buyerReferral: offer.buyerReferral,
            },
          }),
        )

        if (!response.ok) {
          throw new Error("Magic Eden could not create the accept transaction")
        }

        const { v0 } = await wrap(response.json())
        if (!v0?.data) {
          throw new Error("Magic Eden could not create the accept transaction")
        }

        // Send Magic Eden's own v0 message. The legacy variant is oversized and
        // re-encoding it would strip the signer flag off their auction-house
        // cosigner, so it is deliberately not used here. fitSolanaTransactionToPacket
        // is a no-op on a versioned transaction and is kept only so falling back
        // to `txSigned` stays a one-line change.
        const decoded = deserializeSolanaTransaction(Uint8Array.from(v0.data))
        const transaction = await wrap(
          fitSolanaTransactionToPacket({
            connection,
            transaction: decoded,
            lookupTableAddresses: [],
          }),
        )

        const txId = await wrap(
          signAndSendSolanaTransaction({ connection, signer, transaction }),
        )
        return { txId }
      },
      "solanaAcceptMagicEdenOfferAction",
    ).extend(
      withAsync(),
      withCallHook(() => {
        sonnerToast.custom(
          (id) => (
            <AppToast.Pending
              id={id}
              title="Accept offer"
              main={<Trans>Accept offer</Trans>}
              second={<Trans>Pending...</Trans>}
            />
          ),
          { duration: Number.POSITIVE_INFINITY },
        )
      }),
    )

    acceptAction.onReject.extend(
      withCallHook(({ error }) => {
        const message = getTransactionErrorMessage(error)
        sonnerToast.dismiss()
        sonnerToast.custom((id) => (
          <AppToast.Error
            id={id}
            title="Accept offer"
            main={<Trans>Accepting the offer failed</Trans>}
            second={
              <div className="font-semibold text-destructive">{message}</div>
            }
          />
        ))
      }),
    )

    acceptAction.onFulfill.extend(
      withCallHook(({ payload, params: [params] }) => {
        invalidateNftOffersQueries(params.nft.id)
        sonnerToast.dismiss()
        sonnerToast.custom((id) => (
          <AppToast.Success
            id={id}
            title="Accept offer"
            main={<Trans>Offer accepted</Trans>}
            second={
              <SuccessViewTransaction
                transactionId={payload.txId}
                blockchain={Blockchain.SOLANA}
              />
            }
          />
        ))
      }),
    )

    return acceptAction
  }

  return { acceptMagicEdenOfferActionFactory }
})
